import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home, RotateCcw, WifiOff } from 'lucide-react';
import { isStaleChunkReloadInProgress, staleChunkReloadSettled } from '@/lib/chunkReload';
import { useServerReachable } from '@/hooks/useOnlineStatus';
import { isNativePlatform } from '@/lib/platform';
import { RouteFallback } from './RouteFallback';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** A stale-chunk reload may be under way: show loading instead of flashing the error. */
  awaitingReload: boolean;
}

const PRIMARY_BUTTON =
  'inline-flex items-center justify-center gap-2 min-h-11 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2';
const SECONDARY_BUTTON =
  'inline-flex items-center justify-center gap-2 min-h-11 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2';

function DefaultErrorFallback({ onRetry }: { onRetry: () => void }): ReactNode {
  const { t } = useTranslation('common');
  const { reachability } = useServerReachable();
  const unreachable = reachability === 'unreachable';
  // An app has no page to reload: same action, app words (VOL-343).
  const inApp = isNativePlatform();
  const message = inApp ? t('errors.boundary.message_app') : t('errors.boundary.message');
  const offlineMessage = inApp
    ? t('errors.boundary.offline_message_app')
    : t('errors.boundary.offline_message');

  const handleReload = (): void => {
    window.location.reload();
  };

  const handleGoHome = (): void => {
    window.location.assign('/');
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12"
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-full ${
              unreachable ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'
            }`}
            aria-hidden="true"
          >
            {unreachable ? <WifiOff className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          </span>
          <h1 className="text-xl font-bold text-slate-900">
            {unreachable ? t('errors.boundary.offline_title') : t('errors.boundary.title')}
          </h1>
        </div>

        <p className="text-sm text-slate-600 mb-6">
          {unreachable ? offlineMessage : message}
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Reload and home would swap the app (live entry in memory included) for the
              browser's offline page: only offered once the server has answered. A Wi-Fi
              without internet still reports navigator.onLine, hence the probe. */}
          {reachability === 'reachable' && (
            <button type="button" autoFocus onClick={handleReload} className={PRIMARY_BUTTON}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {inApp ? t('errors.boundary.reload_app') : t('errors.boundary.reload')}
            </button>
          )}
          <button
            type="button"
            autoFocus={reachability !== 'reachable'}
            onClick={onRetry}
            className={reachability === 'reachable' ? SECONDARY_BUTTON : PRIMARY_BUTTON}
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            {t('errors.boundary.retry')}
          </button>
          {reachability === 'reachable' && (
            <button type="button" onClick={handleGoHome} className={SECONDARY_BUTTON}>
              <Home className="h-4 w-4" aria-hidden="true" />
              {t('errors.boundary.go_home')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, awaitingReload: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true, awaitingReload: isStaleChunkReloadInProgress() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    if (this.state.awaitingReload) {
      void staleChunkReloadSettled()?.then(() => this.setState({ awaitingReload: false }));
    }
  }

  render(): ReactNode {
    const { hasError, awaitingReload } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    if (awaitingReload) {
      // Same screen as a route loading, so a reload onto the new build reads as loading.
      return <RouteFallback />;
    }

    if (fallback !== undefined) {
      return fallback;
    }

    return <DefaultErrorFallback onRetry={this.retry} />;
  }

  // Renders the children again without reloading: the live entry and the rest of the app
  // state stay in memory.
  private retry = (): void => {
    this.setState({ hasError: false, awaitingReload: false });
  };
}
